import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import "./reviews.css";
import { ShareWindow } from "@/ui/ShareWindow";
import { myTimeZone, createDraft, editReview, getReview, getTeam, saveDraft, sendReview, splitNotes, type ReviewItem, type Suggested, type ReviewSummary, type ShareList, type Teammate } from "./api";
import { ThemeIcon, useReviewTheme } from "./shell";
import { BuilderCards, itemsFromCards, rowsFromCards, type CardRow } from "./BuilderCards";
import { BuilderFiles, itemsFromRows, rowsFromItems, type FileRow } from "./BuilderFiles";

// NR-2 — New review (Specs/Launch Reviews — New Review Builder — PRD (Nov 4, 2026).md). A teammate
// picks a room type and names it (a draft only they see), types the questions, picks reviewers in
// the Share window (Can review by default), previews it as a reviewer would, and clicks Send.
// /reviews/new starts one; /reviews/:id/edit keeps building a draft.
// NR-3: Paste notes turns messy notes into suggested questions (AI on the server, checked there);
// the author edits, deletes, reorders and merges them, pastes more, then adds them to the list.
// NR-4: a Media or Long form review has files instead of questions (BuilderFiles.tsx).
// NR-5: a Short form review has posts and emails (BuilderCards.tsx).

type Kind = ReviewSummary["type"];
const KINDS: { key: Kind; label: string; blurb: string; ready: boolean }[] = [
  { key: "questions", label: "Questions", blurb: "Questions for people to answer, one per card.", ready: true },
  { key: "media", label: "Media", blurb: "Videos for people to watch and approve.", ready: true },
  { key: "long", label: "Long form", blurb: "PDFs for people to read and approve, one per card.", ready: true },
  { key: "short", label: "Short form", blurb: "Posts and emails, one card each.", ready: true },
];
const LEVEL = { review: "Can review", comment: "Can comment", view: "Can view" } as const;

type Q = { key: string; id: string; title: string; details: string; rec: string; choices: string };
let seq = 0;
const blankQ = (): Q => ({ key: `k${++seq}`, id: "", title: "", details: "", rec: "", choices: "" });
// choices are typed as one line, split on "/"
const choiceList = (s: string) => s.split("/").map((c) => c.trim()).filter(Boolean);
const fromSuggested = (x: Suggested): Q => ({ ...blankQ(), title: x.title, details: x.details || "", rec: x.rec || "", choices: (x.choices || []).join(" / ") });
// merging keeps the first question and folds the second into its background
const merged = (a: Q, b: Q): Q => ({
  ...a,
  details: [a.details, `Also: ${b.title}`, b.details].map((t) => t.trim()).filter(Boolean).join(" "),
  choices: [...new Set([...choiceList(a.choices), ...choiceList(b.choices)])].join(" / "),
  rec: a.rec || b.rec,
});
// a question keeps its id once it has one; new ones get the next free q-number
// `taken` is every id the review has ever had: a new item on a deleted item's id would look like an edit of it
const withIds = (qs: Q[], taken: Iterable<string> = []) => {
  const used = new Set([...taken, ...qs.map((q) => q.id).filter(Boolean)]);
  let n = 0;
  const next = () => { do n++; while (used.has(`q${n}`)); used.add(`q${n}`); return `q${n}`; };
  return qs.map((q) => (q.id ? q : { ...q, id: next() }));
};
const toItems = (qs: Q[]): ReviewItem[] =>
  qs.filter((q) => q.title.trim()).map((q) => ({ id: q.id, title: q.title.trim(), ...(choiceList(q.choices).length > 1 ? { choices: choiceList(q.choices) } : {}), ...(q.details.trim() ? { details: q.details.trim() } : {}), ...(q.rec.trim() ? { rec: q.rec.trim() } : {}) }) as ReviewItem);

export function ReviewBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [theme, toggleTheme] = useReviewTheme();
  const [kind, setKind] = useState<Kind>("questions");
  const [title, setTitle] = useState("");
  const [qs, setQs] = useState<Q[]>([]);
  const [files, setFiles] = useState<FileRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const [cards, setCards] = useState<CardRow[]>([]);
  const [invite, setInvite] = useState<ShareList>({ people: [], team: null });
  const [due, setDue] = useState("");
  const [team, setTeam] = useState<Teammate[]>([]);
  const [loaded, setLoaded] = useState(!id);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [problem, setProblem] = useState("");
  const [toast, setToast] = useState("");
  const [picking, setPicking] = useState(false);
  const lastFocus = useRef<number | null>(null);
  const takenIds = useRef<string[]>([]);
  const [pasting, setPasting] = useState(false);
  const [notes, setNotes] = useState("");
  const [splitting, setSplitting] = useState(false);
  const [splitProblem, setSplitProblem] = useState("");
  const [sugg, setSugg] = useState<Q[]>([]);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    getReview(id).then((r) => {
      if (!alive) return;
      // NR-9: a review built here can still be edited once it's out; one Claude built opens in its room
      if (!r.draft && !r.sentAt) return navigate(`/reviews/${encodeURIComponent(id)}`, { replace: true });
      setSent(!r.draft);
      const live = r.items.filter((it) => !it.removed);
      takenIds.current = r.items.map((it) => it.id);
      setKind(r.type); setTitle(r.title); setInvite(r.invite || { people: [], team: null }); setDue(r.due?.date || "");
      setFiles(rowsFromItems(live));
      if (r.type === "short") setCards(rowsFromCards(live));
      else setQs(live.filter((it) => !it.media).map((it) => ({ key: `k${++seq}`, id: it.id, title: it.title || "", details: it.details || "", rec: it.rec || "", choices: (it.choices || []).join(" / ") })));
      setLoaded(true);
    }, (e) => alive && setProblem(e instanceof Error ? e.message : "Couldn't open the draft."));
    getTeam().then((t) => alive && setTeam(t || []), () => {});
    return () => { alive = false; };
  }, [id]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(""), 6000); return () => clearTimeout(t); }, [toast]);

  const start = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return setProblem("Give the review a name.");
    setBusy(true); setProblem("");
    try {
      const { id: newId } = await createDraft({ title: title.trim(), type: kind });
      navigate(`/reviews/${encodeURIComponent(newId)}/edit`, { replace: true });
    } catch (err) { setProblem(err instanceof Error ? err.message : "Couldn't start the review."); }
    setBusy(false);
  };

  const hasFiles = kind === "media" || kind === "long";
  const itemsNow = () => {
    const named = withIds(qs, takenIds.current);
    setQs(named);
    const items = kind === "short" ? itemsFromCards(cards, takenIds.current) : hasFiles ? itemsFromRows(files, takenIds.current) : toItems(named);
    if (hasFiles) setFiles((rs) => rs.map((r, i) => ({ ...r, id: items[i]?.id || r.id })));
    if (kind === "short") setCards(rowsFromCards(items));
    return items;
  };
  const save = async () => {
    const items = itemsNow();
    await saveDraft(id!, { title: title.trim() || "Untitled review", items, due: due ? { date: due, tz: myTimeZone() } : null });
    return items;
  };
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setProblem("");
    try { await fn(); } catch (err) { setProblem(err instanceof Error ? err.message : "Couldn't save. Try again."); }
    setBusy(false);
  };
  const saveOnly = () => run(async () => { await save(); setToast("Draft saved. Only you can see it."); });
  const preview = () => run(async () => { await save(); navigate(`/reviews/${encodeURIComponent(id!)}`); });
  const send = () => run(async () => {
    const items = await save();
    if (!items.length) return setProblem(`Add at least one ${kind === "media" ? "video" : kind === "long" ? "PDF" : kind === "short" ? "post or email" : "question"} before you send it.`);
    if (!invite.people.length && !invite.team) return setProblem("Pick at least one reviewer.");
    await sendReview(id!);
    navigate(`/reviews/${encodeURIComponent(id!)}`, { replace: true, state: { sent: true } });
  });

  const saveChanges = () => run(async () => {
    const { emailed } = await editReview(id!, { title: title.trim() || "Untitled review", items: itemsNow() });
    const told = emailed.length ? ` We emailed ${emailed.length === 1 ? "the 1 person" : `the ${emailed.length} people`} who had already finished.` : "";
    navigate(`/reviews/${encodeURIComponent(id!)}`, { replace: true, state: { toast: `Changes saved.${told}` } });
  });

  const edit = (i: number, patch: Partial<Q>) => setQs((xs) => xs.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const move = (i: number, by: number) => setQs((xs) => { const to = i + by; if (to < 0 || to >= xs.length) return xs; const ys = [...xs]; [ys[i], ys[to]] = [ys[to], ys[i]]; return ys; });
  const add = () => { setQs((xs) => [...xs, blankQ()]); lastFocus.current = qs.length; };
  useEffect(() => {
    if (lastFocus.current == null) return;
    (document.getElementById(`nrq-${lastFocus.current}`) as HTMLTextAreaElement | null)?.focus();
    lastFocus.current = null;
  }, [qs.length]);

  const splitNow = async () => {
    if (!notes.trim()) return setSplitProblem("Paste some notes first.");
    setSplitting(true); setSplitProblem("");
    try {
      const got = await splitNotes(notes);
      if (!got.length) setSplitProblem("No questions came out of those notes. Try adding more detail.");
      setSugg((xs) => [...xs, ...got.map(fromSuggested)]);
      if (got.length) setNotes("");
    } catch (err) { setSplitProblem(err instanceof Error ? err.message : "Couldn't turn those notes into questions. Try again."); }
    setSplitting(false);
  };
  const editS = (i: number, patch: Partial<Q>) => setSugg((xs) => xs.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const moveS = (i: number, by: number) => setSugg((xs) => { const to = i + by; if (to < 0 || to >= xs.length) return xs; const ys = [...xs]; [ys[i], ys[to]] = [ys[to], ys[i]]; return ys; });
  const mergeS = (i: number) => setSugg((xs) => (i + 1 < xs.length ? [...xs.slice(0, i), merged(xs[i], xs[i + 1]), ...xs.slice(i + 2)] : xs));
  const keep = sugg.filter((q) => q.title.trim());
  const addSugg = () => { setQs((xs) => [...xs.filter((q) => q.title.trim() || q.details.trim() || q.rec.trim()), ...keep]); setSugg([]); setPasting(false); setSplitProblem(""); };

  const nameOf = (email: string) => team.find((m) => m.email.toLowerCase() === email.toLowerCase())?.name || email;
  const reviewers = [...(invite.team ? [`Everyone on the team · ${LEVEL[invite.team]}`] : []), ...invite.people.map((p) => `${nameOf(p.email)} · ${LEVEL[p.level]}`)];

  const header = (
    <header className="lr-hdr">
      <Link className="back" to="/reviews" aria-label="Back to the Review Center">‹</Link>
      <div className="grow">
        <h1>{sent ? "Edit your review" : id ? "Build your review" : "New review"}</h1>
        <div className="sub">{sent ? "Already sent · reviewers see your changes when you save" : id ? "Draft · only you can see it until you send it" : "Pick what kind of review it is and name it"}</div>
      </div>
      <button className="icon" onClick={toggleTheme} aria-label={theme === "dark" ? "Light mode" : "Dark mode"}><ThemeIcon theme={theme} /></button>
    </header>
  );

  if (!id) {
    return (
      <div className="lr" data-theme={theme}>
        {header}
        <main className="lr-scroll">
          <form className="nr" onSubmit={start}>
            <fieldset className="nr-kinds">
              <legend>What are people reviewing?</legend>
              {KINDS.map((k) => (
                <label key={k.key} className={`nr-kind ${kind === k.key ? "on" : ""} ${k.ready ? "" : "soon"}`}>
                  <input type="radio" name="kind" value={k.key} checked={kind === k.key} disabled={!k.ready} onChange={() => setKind(k.key)} />
                  <b>{k.label}</b>
                  <span>{k.ready ? k.blurb : `${k.blurb} Coming soon.`}</span>
                </label>
              ))}
            </fieldset>
            <label className="nr-field">
              <span>Name</span>
              <input aria-label="Review name" value={title} placeholder="e.g. Spring launch questions" onChange={(e) => setTitle(e.target.value)} autoFocus />
            </label>
            {problem && <p className="nr-problem" role="alert">{problem}</p>}
            <div className="nr-actions"><button type="submit" className="nr-primary" disabled={busy}>Start</button></div>
          </form>
        </main>
      </div>
    );
  }

  return (
    <div className="lr" data-theme={theme}>
      {header}
      <main className="lr-scroll">
        {!loaded ? (problem ? <div className="lr-error">{problem}</div> : <div className="lr-empty"><span>Opening the draft…</span></div>) : (
          <div className="nr">
            <label className="nr-field">
              <span>Name</span>
              <input aria-label="Review name" value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>

            {kind === "short" ? <BuilderCards rows={cards} onChange={setCards} /> : hasFiles ? <BuilderFiles kind={kind as "media" | "long"} reviewName={title} rows={files} onChange={setFiles} onUploading={setUploading} /> : (
            <section className="nr-sec" aria-label="Questions">
              <h2>Questions <small>{toItems(qs).length}</small></h2>
              {!qs.length && <p className="nr-hint">Add the questions you need answered. Each one becomes its own card.</p>}
              <ol className="nr-qs">
                {qs.map((q, i) => (
                  <li key={q.key} className="nr-q">
                    <div className="nr-qtop">
                      <span className="nr-n">{i + 1}</span>
                      <textarea id={`nrq-${i}`} aria-label={`Question ${i + 1}`} rows={2} value={q.title} placeholder="The question, in one plain sentence" onChange={(e) => edit(i, { title: e.target.value })} />
                      <span className="nr-qtools">
                        <button type="button" aria-label={`Move question ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                        <button type="button" aria-label={`Move question ${i + 1} down`} disabled={i === qs.length - 1} onClick={() => move(i, 1)}>↓</button>
                        <button type="button" aria-label={`Delete question ${i + 1}`} className="danger" onClick={() => setQs((xs) => xs.filter((_, j) => j !== i))}>✕</button>
                      </span>
                    </div>
                    <input aria-label={`Choices for question ${i + 1}`} value={q.choices} placeholder="Choices, separated by / (optional)" onChange={(e) => edit(i, { choices: e.target.value })} />
                    <input aria-label={`Background for question ${i + 1}`} value={q.details} placeholder="Background: why you're asking (optional)" onChange={(e) => edit(i, { details: e.target.value })} />
                    <input aria-label={`Recommendation for question ${i + 1}`} value={q.rec} placeholder="Your recommendation, if you have one (optional)" onChange={(e) => edit(i, { rec: e.target.value })} />
                  </li>
                ))}
              </ol>
              <div className="nr-row">
                <button type="button" className="nr-add" onClick={add}>Add a question</button>
                {!pasting && <button type="button" className="nr-ghost" onClick={() => setPasting(true)}>Paste notes</button>}
              </div>
              {pasting && (
                <div className="nr-paste">
                  <label className="nr-field">
                    <span>{sugg.length ? "Paste more notes" : "Paste your notes. We'll turn them into questions you can edit."}</span>
                    <textarea aria-label="Your notes" rows={6} value={notes} placeholder="Meeting notes, a Slack thread, a list of open items…" onChange={(e) => setNotes(e.target.value)} />
                  </label>
                  {splitProblem && <p className="nr-problem" role="alert">{splitProblem}</p>}
                  <div className="nr-row">
                    <button type="button" className="nr-primary" disabled={splitting} onClick={splitNow}>{splitting ? "Reading your notes…" : "Turn into questions"}</button>
                    <button type="button" className="nr-ghost" onClick={() => { setPasting(false); setSugg([]); setNotes(""); setSplitProblem(""); }}>Cancel</button>
                  </div>
                  {!!sugg.length && (
                    <section className="nr-sugg" aria-label="Suggested questions">
                      <h3>Suggested questions <small>{keep.length}</small></h3>
                      <p className="nr-hint">Check each one. A recommendation only shows when your notes point to it.</p>
                      <ol className="nr-qs">
                        {sugg.map((q, i) => (
                          <li key={q.key} className="nr-q">
                            <div className="nr-qtop">
                              <span className="nr-n">{i + 1}</span>
                              <textarea aria-label={`Suggested question ${i + 1}`} rows={2} value={q.title} onChange={(e) => editS(i, { title: e.target.value })} />
                              <span className="nr-qtools">
                                <button type="button" aria-label={`Move suggestion ${i + 1} up`} disabled={i === 0} onClick={() => moveS(i, -1)}>↑</button>
                                <button type="button" aria-label={`Move suggestion ${i + 1} down`} disabled={i === sugg.length - 1} onClick={() => moveS(i, 1)}>↓</button>
                                <button type="button" aria-label={`Merge suggestion ${i + 1} with the next one`} disabled={i === sugg.length - 1} onClick={() => mergeS(i)}>⤓</button>
                                <button type="button" aria-label={`Delete suggestion ${i + 1}`} className="danger" onClick={() => setSugg((xs) => xs.filter((_, j) => j !== i))}>✕</button>
                              </span>
                            </div>
                            {(q.choices || q.rec || q.details) && (
                              <div className="nr-meta">
                                {q.choices && <span>Choices: {choiceList(q.choices).join(" · ")}</span>}
                                {q.rec && <span>Recommended: {q.rec}</span>}
                                {q.details && <span>{q.details}</span>}
                              </div>
                            )}
                          </li>
                        ))}
                      </ol>
                      <button type="button" className="nr-primary" disabled={!keep.length} onClick={addSugg}>{`Add ${keep.length} question${keep.length === 1 ? "" : "s"}`}</button>
                    </section>
                  )}
                </div>
              )}
            </section>
            )}

            {!sent && (
            <section className="nr-sec" aria-label="Reviewers">
              <h2>Reviewers</h2>
              {reviewers.length ? <ul className="nr-who">{reviewers.map((r) => <li key={r}>{r}</li>)}</ul> : <p className="nr-hint">Nobody yet. They're emailed when you click Send, not before.</p>}
              <button type="button" className="nr-ghost" onClick={() => setPicking(true)} aria-haspopup="dialog">Pick reviewers</button>
              <label className="nr-field nr-due">Due date (optional)
                <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
                <span className="nr-hint">Anyone who hasn't finished gets one reminder at 9am the day before.</span>
              </label>
            </section>
            )}
            {sent && <p className="nr-hint">If you change something someone already answered, their answer clears and they're asked to look again. Anyone who already finished gets one email about new or changed items.</p>}

            {problem && <p className="nr-problem" role="alert">{problem}</p>}
            <div className="nr-actions">
              {sent ? <button type="button" className="nr-primary" disabled={busy || uploading} onClick={saveChanges}>Save changes</button> : <>
              <button type="button" className="nr-ghost" disabled={busy || uploading} onClick={saveOnly}>Save draft</button>
              <button type="button" className="nr-ghost" disabled={busy || uploading} onClick={preview}>Preview</button>
              <button type="button" className="nr-primary" disabled={busy || uploading} onClick={send}>Send</button>
              </>}
            </div>
          </div>
        )}
      </main>
      {picking && (
        <ShareWindow title={`Who reviews “${title || "this review"}”`} onClose={() => setPicking(false)} onToast={setToast}
          people={{
            team: getTeam,
            load: async () => invite,
            save: async (l) => { const out = await saveDraft(id, { invite: { people: l.people, team: l.team } }); setInvite(out.invite); return out.invite; },
            hint: `Nobody is told until you click Send. Can review lets them approve, ask for changes or reject each ${hasFiles ? "file" : kind === "short" ? "card" : "question"}.`,
          }} />
      )}
      {toast && <div className="lr-toast" role="status"><span>{toast}</span></div>}
    </div>
  );
}
